<template>
  <v-sheet
    :color="`grey ${theme.isDark ? 'darken-2' : 'lighten-4'}`"
    class="pa-3"
    v-if="paymentInfo.isLoading"
  >
    <v-skeleton-loader
      class="mx-auto"
      type="article,article,article,article,article,article"
      max-height="775"
    ></v-skeleton-loader>
  </v-sheet>
  <aside v-else>
    <h2 class="invoice-title">{{ $t("reservation.summary") }}</h2>
    <div class="invoice-details d-flex flex-column gap-4">
      <div class="invoice-detail d-flex flex-column gap-md-2 gap-3">
        <h3 class="invoice-heading m-0">{{ $t("reservation.courseName") }}</h3>
        <p class="invoice-desc m-0">
          {{ getCoursesNames }}
        </p>
      </div>
      <div class="invoice-detail d-flex flex-column gap-md-2 gap-3">
        <h3 class="invoice-heading m-0">{{ $t("reservation.programName") }}</h3>
        <p class="invoice-desc m-0">
          {{ paymentInfo.payment.program_name }}
        </p>
      </div>
      <div class="invoice-detail d-flex flex-column gap-md-2 gap-3">
        <h3 class="invoice-heading m-0">
          {{ $t("reservation.programDescription") }}
        </h3>
        <p class="invoice-desc m-0">
          {{ paymentInfo.payment.program_description }}
        </p>
      </div>
      <div
        class="invoice-detail discount-detail d-flex flex-column gap-md-2 gap-3"
      >
        <h3 class="invoice-heading m-0">
          {{ $t("reservation.discountAmount") }}
        </h3>
        <p class="invoice-desc m-0">
          {{ paymentAmount.total_discount }}
          {{ $t(`currency.${currency}`) }}
        </p>
      </div>
      <div
        class="invoice-detail discount-detail d-flex flex-column gap-md-2 gap-3"
      >
        <h3 class="invoice-heading m-0">{{ $t("reservation.vat") }}</h3>
        <p class="invoice-desc m-0">
          {{ paymentAmount.vat_amount }}
          {{ $t(`currency.${currency}`) }}
        </p>
      </div>
      <div
        class="invoice-detail discount-detail d-flex flex-column gap-md-2 gap-3"
      >
        <h3 class="invoice-heading m-0">
          {{ $t("reservation.discountPercentage") }}
        </h3>
        <p class="invoice-desc m-0">
          {{ paymentAmount.total_discount_percentage }} %
        </p>
      </div>
      <div class="invoice-detail cost-detail d-flex flex-column gap-md-2 gap-3">
        <h3 class="invoice-heading m-0">{{ $t("reservation.totalCost") }}</h3>
        <p class="invoice-desc m-0">
          {{ $t("reservation.cost") }}:
          {{ paymentAmount.net_amount }}
          {{ $t(`currency.${currency}`) }}
        </p>
      </div>
      <hr class="m-0" />
      <div class="time-slots">
        <h3 class="time-slots-title">{{ $t("reservation.timeslotsSheet") }}</h3>
        <div class="time-slot-range d-flex flex-column gap-2">
          <div class="d-flex justify-content-between">
            <h4>{{ $t("reservation.startDate") }}</h4>
            <h4>{{ $t("reservation.endDate") }}</h4>
          </div>
          <div
            class="d-flex justify-content-between"
            v-for="(timeSlot, index) in timeSlots"
            :key="index"
          >
            <p class="d-flex gap-2">
              <span>{{ timeSlot.startDay }}</span>
              <span>{{ timeSlot.start_date_time }}</span>
            </p>
            <p class="d-flex gap-2">
              <span>{{ timeSlot.endDay }}</span>
              <span>{{ timeSlot.end_date_time }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import moment from "moment";

export default {
  name: "InvoiceDetails",
  data() {
    return {
      institute_configs: JSON.parse(localStorage.getItem("institute_configs")),
    };
  },
  inject: {
    theme: {
      default: { isDark: false },
    },
  },
  /**
   * Initialize Component
   */
  mounted() {
    window.addEventListener("localstorageChange", (event) => {
      this.institute_configs = JSON.parse(
        event.detail.storage.institute_configs
      );
    });
  },
  computed: {
    /**
     * Get Payment Info and Payment Intent from state
     */
    paymentInfo() {
      return this.$store.getters.GET_PAYMENT_INFO;
    },
    /**
     * Get courses names if there's more that one
     */
    getCoursesNames() {
      const names = [
        this.paymentInfo.payment.first_course?.title,
        this.paymentInfo.payment.second_course?.title,
      ];
      return names.filter((name) => name).join(",");
    },
    /**
     * Get payment amount
     */
    paymentAmount() {
      return this.paymentInfo.payment.amount_data;
    },
    /**
     * Get sorted time slots
     */
    timeSlots() {
      return this.paymentInfo.payment.student_available_time_slots
        ?.map((timeslot) => {
          return {
            ...timeslot,
            startDay: moment(timeslot.start_date_time + new Date().getFullYear()).format("ddd"),
            endDay: moment(timeslot.end_date_time + new Date().getFullYear()).format("ddd"),
          };
        })
        .sort((a, b) => {
          return new Date(a.start_date_time).getTime() >
            new Date(b.start_date_time).getTime()
            ? 1
            : -1;
        });
    },
    /**
     * Get institute currency if there's any
     */
    currency() {
      return this.institute_configs?.currency
        ? this.institute_configs?.currency
        : "USD";
    },
  },
};
</script>

<style lang="scss">
:root {
  font-size: 16px;
}

@media all and (max-width: $min-md-screen) {
  :root {
    font-size: 12.8px;
  }
}
</style>

<style lang="scss" scoped>
aside {
  border: 1px solid $description;
  border-radius: 8px;
  padding: 16px;
  font-family: $font-family;
  font-style: normal;
  font-weight: 500;
  .invoice-title {
    font-size: 1.25rem;
    line-height: 1.75rem;
    color: #061c3d;
    margin-bottom: 36px;
  }
  .invoice-details {
    .invoice-detail {
      .invoice-heading {
        font-size: 1rem;
        line-height: 1.25rem;
        color: #061c3d;
      }
      .invoice-desc {
        font-weight: 400;
        font-size: 1rem;
        line-height: 1.5rem;
        color: #42526b;
      }
      &.discount-detail {
        .invoice-heading {
          color: $error;
        }
        .invoice-desc {
          color: $light-error;
        }
      }
      &.cost-detail {
        .invoice-heading {
          color: $success;
        }
        .invoice-desc {
          color: $light-success;
        }
      }
    }
    .time-slots {
      font-family: $font-family;
      font-style: normal;
      .time-slots-title {
        font-weight: 500;
        font-size: 1.25rem;
        line-height: 1.75rem;
        color: #061c3d;
        margin-bottom: 24px;
      }

      .time-slot-range {
        h4 {
          font-weight: 500;
          font-size: 1.25rem;
          line-height: 1.75rem;
          color: #061c3d;
          margin: 0;
        }
        p {
          font-weight: 400;
          font-size: 1rem;
          line-height: 1.5rem;
          color: $paragraph;
          margin: 0;
        }
      }
    }
  }
}

@media all and (max-width: $max-md-screen) and (min-width: $min-md-screen) {
  aside {
    .invoice-details {
      .time-slots {
        .time-slot-range {
          .slot-date {
            h4 {
              font-size: 17px;
            }
          }
        }
      }
    }
  }
}
</style>
